import { Box, DialogContent, DialogTitle, Stack, styled } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import {
  ComposableDashboardFiltersEnum,
  IComposableAnalyticsGlobalFilters,
  IComposableDashboardCollectionsContext,
  IHomeRecruiterAnalytics,
  IPinnedDashboardCollection,
  getMyPinnedDashboardsCollection,
  getRecruiterAnalyticsStatus,
} from 'wisestep-api';
import {
  IUserDTO,
  WsButton,
  WsColors,
  WsDialog,
  WsTypoColors,
  WsTypoVariants,
  WsTypography,
} from 'wisestep-base';

import { useUserContext, useWorkspaceContext } from '../../context';
import { defaultPinnedDashboardCollection } from '../../features/analyticsPage/helper/getDefaultDashboardValues';
import { useComposableDashboardCollectionsContext } from '../../features/analyticsPage/hooks/useComposableDashboardCollectionsContext';
import usePinnedDashboardCollections from '../../features/analyticsPage/hooks/usePinnedDashboardCollections';
import usePromptText from '../../features/analyticsPage/hooks/usePromptText';
import { GetDashboardFilters } from '../../features/teams/helper/GetDashboardFilters';
import RoundPin from '../../images/RoundPin';
import ErrorLogger from '../../providers/ErrorLogger';
import { failed } from '../../utils/NotificationMessages';
import { useNotificationStore } from '../../utils/NotificationStore';

import { defaultPinRecruiterAnalyticsData, pinRecruiterAnalytics } from './PinDashboardHelper';

export interface DashboardCollectionIdType {
  dashboardIds: string[];
  type: string;
}
export interface IPinDashboardCollectionButtonProps {
  filters: Record<ComposableDashboardFiltersEnum, IComposableAnalyticsGlobalFilters> | null;
  entityId: string | null;
  entityName: string;
  tabChange?: (string) => void;
  homeUnpin?: boolean;
  setCollect?: boolean;
  pinDashboardId?: string;
  dashbaordsCollectionIds?: DashboardCollectionIdType;
  isPinned?: boolean;
  fromProfile?: boolean;
}

const StyledBox = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'flex-end',
  borderRadius: 0.45,
}));

const PinDashboardCollectionButton = ({
  filters,
  entityId,
  entityName,
  tabChange,
  pinDashboardId,
  homeUnpin = false,
  setCollect = false,
  dashbaordsCollectionIds,
  isPinned,
  fromProfile = false,
}: IPinDashboardCollectionButtonProps) => {
  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const { pinPromptText, unPinPromptText } = usePromptText(entityName);
  const unpin = useRef<boolean | undefined>(isPinned);
  const workspace = useWorkspaceContext();
  const [pinAnalyticsStatus, setPinAnalyticsStatus] = useState<boolean>(false);
  const { selectedDashboardCollection } =
    useComposableDashboardCollectionsContext() as Required<IComposableDashboardCollectionsContext>;
  const {
    pinnedDashboards,
    selectedPinnedDashboard,
    setSelectedPinnedDashboard,
    handlePinningDashboard,
    openPinDialog,
    setOpenPinDialog,
    isDashboardPin,
  } = usePinnedDashboardCollections(false);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const isTargetWorkspace = workspace.id == 'a61ed280-61fa-4e19-8e54-88bffcc24a75';
  const forRecruiterAnalytics = fromProfile && isTargetWorkspace;
  const [pinRecruiterAnalyticsData, setPinRecruiterAnalyticsData] =
    useState<IHomeRecruiterAnalytics>(defaultPinRecruiterAnalyticsData);

  useEffect(() => {
    unpin.current = isPinned;
  }, [isPinned, isDashboardPin]);

  const pinnedDashboardCollection = useMemo(
    () =>
      pinnedDashboards?.filter(
        (pd) =>
          pd.dashboardCollection?.id == selectedDashboardCollection.id &&
          pd.users?.includes(loggedInUser?.email ?? '') &&
          pd.entityId == entityId
      ),
    [entityId, loggedInUser?.email, pinnedDashboards, selectedDashboardCollection.id]
  );

  const isDashboardPinned: boolean = useMemo(
    () => selectedPinnedDashboard?.entityId == entityId || !isEmpty(pinnedDashboardCollection),
    [entityId, pinnedDashboardCollection, selectedPinnedDashboard?.entityId]
  );

  const handlePinDashboard = () => {
    handlePinningDashboard(
      selectedDashboardCollection?.id ?? '',
      {
        filters: filters,
        entityId: entityId,
        entityName: entityName,
      } as unknown as IPinnedDashboardCollection,
      homeUnpin ? pinDashboardId : pinnedDashboardCollection?.[0]?.id
    );
    tabChange && tabChange(selectedDashboardCollection?.id);
  };

  const pinningDashboards = async () => {
    try {
      const getFilters = await GetDashboardFilters({
        param: dashbaordsCollectionIds?.type,
        payload: { id: entityId },
      });

      if (getFilters) {
        const dashboardIds = dashbaordsCollectionIds && dashbaordsCollectionIds?.dashboardIds;
        if (dashboardIds && dashboardIds?.length > 0) {
          dashboardIds.map((id) =>
            handlePinningDashboard(
              id,
              {
                filters: getFilters,
                entityId: entityId,
                entityName: entityName,
              } as unknown as IPinnedDashboardCollection,
              ''
            )
          );
        }
      }
    } catch (error) {
      unpin.current = false;
      setNotification(failed(`Failed to Unpin`));
    }
  };
  useEffect(() => {
    (async () => {
      if (isTargetWorkspace) {
        try {
          const res = await getRecruiterAnalyticsStatus();
          setPinRecruiterAnalyticsData(res.data);
          setPinAnalyticsStatus(res.data?.pinned);
        } catch (error) {
          console.log(error);
        }
      }
    })();
  }, [isTargetWorkspace]);

  const onConfirm = () => {
    if (setCollect) {
      if (dashboardPinned) {
        unpinningDashboard();
        unpin.current = false;
      } else {
        pinningDashboards();
        if (isDashboardPin) unpin.current = true;
      }
    } else if (forRecruiterAnalytics) {
      pinRecruiterAnalytics(
        setPinRecruiterAnalyticsData,
        setPinAnalyticsStatus,
        setNotification,
        pinRecruiterAnalyticsData,
        pinAnalyticsStatus
      );
      setOpenPinDialog(false);
    } else handlePinDashboard();
  };

  const unpinningDashboard = () => {
    const DashboardInPinnedList = async () => {
      try {
        const responseCollection = await getMyPinnedDashboardsCollection();
        if (responseCollection.data?.length > 0) {
          const collection = responseCollection.data;
          collection.map((item) => {
            if (item.entityId === entityId) {
              handlePinningDashboard(
                item.entityId,
                {
                  filters: filters,
                  entityId: entityId,
                  entityName: entityName,
                } as unknown as IPinnedDashboardCollection,
                item.id
              );
            }
          });
        }
      } catch (error) {
        unpin.current = true;
        setNotification(failed(`Failed to Unpin`));
      }
    };
    DashboardInPinnedList();
  };

  const dashboardPinned = forRecruiterAnalytics
    ? pinAnalyticsStatus
    : isDashboardPinned || unpin.current;

  return (
    <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
      <StyledBox>
        <WsTypography
          hoverText={dashboardPinned ? 'Unpin from Home' : 'Pin to Home'}
          bold={true}
          display="inline"
          color={WsTypoColors.SECONDARY}
        >
          {dashboardPinned ? (
            <WsButton
              variant={setCollect ? 'text' : 'outlined'}
              onClick={() => setOpenPinDialog(true)}
              startIcon={!homeUnpin ? <RoundPin color={WsColors.TheBlues500} /> : <></>}
            >
              {homeUnpin ? <RoundPin color={WsColors.TheBlues500} /> : 'Remove from home'}
            </WsButton>
          ) : (
            <WsButton
              variant={homeUnpin ? 'text' : 'contained'}
              onClick={() => setOpenPinDialog(true)}
              startIcon={!homeUnpin ? <RoundPin color={WsColors.White} /> : <></>}
            >
              {homeUnpin ? <RoundPin color={WsColors.GreyBlack} /> : 'Pin To Home'}
            </WsButton>
          )}
        </WsTypography>
      </StyledBox>
      {openPinDialog && (
        <WsDialog openDialog={openPinDialog}>
          <DialogContent sx={{ minHeight: '150px', bgcolor: 'white' }}>
            <DialogTitle sx={{ textAlign: 'center', fontSize: '1.2rem', color: 'black' }}>
              {dashboardPinned ? 'Remove Dashboard from Home Page' : 'Pin Dashboard to Home Page'}
            </DialogTitle>
            <Stack spacing={5} alignItems="center">
              <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.TERTIARY900}>
                {`${dashboardPinned ? unPinPromptText : pinPromptText}`}
              </WsTypography>
              <Stack direction="row" spacing={2} justifyContent="center">
                <WsButton
                  onClick={() => {
                    setSelectedPinnedDashboard(defaultPinnedDashboardCollection);
                    setOpenPinDialog(false);
                  }}
                  variant="text"
                  size="large"
                >
                  Cancel
                </WsButton>
                <WsButton onClick={onConfirm} variant="contained" size="large">
                  {`${dashboardPinned ? 'Yes, Remove' : 'Yes, Pin'}`}
                </WsButton>
              </Stack>
            </Stack>
          </DialogContent>
        </WsDialog>
      )}
    </ErrorBoundary>
  );
};

export default PinDashboardCollectionButton;
